// PC /dashboard 用の AI insight API クライアント(2026-09-15)。
//
// Front-end がすでに Dashboard で取得済みの 5 モジュール集約データ
// (ga4 / gbp / mail / instagram / wordpress) を POST し、
// 構造化 AI insight JSON (actions[] + summary) を受け取る。
//
// AI engine 失敗時は backend が fallback=True で 200 を返すので
// クライアント側は status / fallback フラグを意識しなくて良い。

import { apiFetch } from "./client";

export type InsightAction = {
  priority: "high" | "medium" | "low";
  title: string;
  reason: string;
  targetModule: string;
  buttonLabel: string;
};

export type InsightSummary = {
  overall: string;
  observations: string[];
  watchPoints: string[];
};

export type MailSample = {
  from_name: string | null;
  from_email: string;
  subject: string | null;
  preview: string | null;
  received_at: string | null;
  status: "unhandled" | "handling" | "completed";
  is_read: boolean;
  account_email: string | null;
};

export type InsightRequest = {
  ga4?: {
    kpis?: {
      screenPageViews?: number | null;
      totalUsers?: number | null;
      conversions?: number | null;
    };
    pages?: Array<{ path: string; pv: number }>;
  };
  gbp?: {
    metrics30d?: {
      searches?: number;
      websiteClicks?: number;
      calls?: number;
      directions?: number;
    };
  };
  mail?: {
    shared_mail_count?: number;
    unread_count?: number;
    unhandled_count?: number;
    /**
     * 直近の共有メール sample(最大 30 件)。AI が sender / subject を見て
     * 顧客問い合わせ vs 商品推销を判別する材料に使う。
     */
    recent_messages?: MailSample[];
  };
  instagram?: {
    pending_dm?: number;
    pending_comments?: number;
    followers?: number;
  };
  wordpress?: {
    publish?: number;
    draft?: number;
  };
};

export type DashboardAiInsight = {
  status: "succeeded" | "fallback";
  fallback: boolean;
  text: string;
  usage: Record<string, unknown>;
  actions: InsightAction[];
  summary: InsightSummary;
};

export const dashboardApi = {
  generateAiInsight: (body: InsightRequest) =>
    apiFetch<DashboardAiInsight>("/dashboard/ai-insight", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

// targetModule → 内部ルーティング helper。
// AI が返した targetModule を実際の URL に解決する。
const TARGET_ROUTE_MAP: Record<string, string> = {
  mail: "/mail-v2",
  analytics: "/analytics",
  "google-business": "/google-business",
  instagram: "/instagram-v2",
  site: "/site",
  blog: "/blog",
  "ai-blog": "/ai-blog",
  chatbot: "/chatbot",
  dashboard: "/dashboard",
  settings: "/settings",
};

export function resolveTargetRoute(targetModule: string | null | undefined): string {
  if (!targetModule) return "/dashboard";
  return TARGET_ROUTE_MAP[targetModule] ?? "/dashboard";
}
